import React from "react";
import { Link, useParams } from "react-router-dom";
import SEO from "@/components/SEO";
import { SEO_KEYWORDS } from "@/seo/seoData";
import { motion } from "framer-motion";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, ArrowRight, Calendar, Clock, Search } from "lucide-react";
import { Input } from "@/components/ui/input";

const API_BASE = "https://blog.squaretrigger.com/wp-json/wp/v2";
const HERO_IMAGE = "/blog.webp";

const DEFAULT_BLOG_OG_IMAGE = "https://squaretrigger.com/logo3.webp";

const BLOG_SEO_OVERRIDES = [
  {
    matchedKeywords: [
      "how seo services drive success",
      "seo services drive success",
      "seo services vancouver",
      "seo agency",
      "choose the right seo agency",
      "seo can drive success",
      "seo success",
    ],
    title: "SEO services Vancouver :Get Insights |Square Trigger",
    description:
      "Learn how SEO can drive success for businesses. Get the helpful insights and choose the right SEO agency. Download our comprehensive checklist now.",
  },
  {
    matchedKeywords: [
      "how to increase site traffic",
      "increase site traffic",
      "increasing web traffic",
      "web traffic",
      "site traffic",
      "technical seo",
      "local seo",
      "backlink strategy",
    ],
    title: "Insights on Increasing Web traffic for Website | Square Trigger",
    description:
      "Want More site traffic? Here's a comprehensive guide on Technical SEO, Local SEO, Keywords, and a Good Backlink Strategy. Drive more traffic today.",
  },
  {
    matchedKeywords: [
      "what does website maintenance include",
      "website maintenance include",
      "website maintenance services",
      "website maintenance",
      "maintenance package",
      "website maintenance and support services",
      "maintenance and support",
    ],
    title: "Website maintenance services :Insights |Square Trigger",
    description:
      "Need website maintenance and support services? Here's what a maintenance package must include. Get your website optimized, secure, and effective today.",
  },
];

const BLOG_IMAGE_ALT_OVERRIDES = [
  {
    matchedKeywords: [
      "how seo services drive success",
      "seo services drive success",
      "seo services",
      "seo success",
    ],
    alt: "SEO Services Success Blog1",
  },
  {
    matchedKeywords: [
      "how to increase site traffic",
      "increase site traffic",
      "increasing web traffic",
      "site traffic",
      "web traffic",
    ],
    alt: "Increase Site Traffic Blog2",
  },
  {
    matchedKeywords: [
      "what does website maintenance include",
      "website maintenance include",
      "website maintenance",
      "maintenance include",
    ],
    alt: "Website Maintenance Include Blog3",
  },
];

function stripHtml(html = "") {
  const temp = document.createElement("div");
  temp.innerHTML = html;
  return temp.textContent || temp.innerText || "";
}

function decodeHtml(html = "") {
  const temp = document.createElement("textarea");
  temp.innerHTML = html;
  return temp.value;
}

function removeFirstImageFromContent(html = "") {
  const temp = document.createElement("div");
  temp.innerHTML = html;

  const firstImageBlock =
    temp.querySelector("figure.wp-block-image") ||
    temp.querySelector("figure:has(img)") ||
    temp.querySelector("p img")?.closest("p") ||
    temp.querySelector("img");

  if (firstImageBlock) {
    firstImageBlock.remove();
  }

  return temp.innerHTML;
}

function formatDate(dateString) {
  if (!dateString) return "";

  const date = new Date(dateString);

  return date.toLocaleDateString("en-CA", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function getFeaturedImage(post) {
  return (
    post?._embedded?.["wp:featuredmedia"]?.[0]?.source_url ||
    post?._embedded?.["wp:featuredmedia"]?.[0]?.media_details?.sizes?.full
      ?.source_url ||
    post?._embedded?.["wp:featuredmedia"]?.[0]?.media_details?.sizes?.large
      ?.source_url ||
    "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?q=80&w=1600&auto=format&fit=crop"
  );
}

function getCategoryNames(post) {
  return (
    post?._embedded?.["wp:term"]
      ?.flat()
      ?.filter((item) => item?.taxonomy === "category")
      ?.map((item) => item?.name)
      ?.filter(Boolean) || []
  );
}

function getPrimaryCategory(post) {
  const categories = getCategoryNames(post);
  return categories[0] || "Blog";
}

function getAuthorName(post) {
  return post?._embedded?.author?.[0]?.name || "Square Trigger Team";
}

function getReadTime(post) {
  const text = stripHtml(post?.content?.rendered || post?.excerpt?.rendered || "");
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  const minutes = Math.max(1, Math.ceil(words / 200));
  return `${minutes} min read`;
}

function normalizePost(post) {
  return {
    id: post.id,
    slug: post.slug,
    title: decodeHtml(post.title?.rendered || "Untitled"),
    excerpt: stripHtml(post.excerpt?.rendered || ""),
    content: removeFirstImageFromContent(post.content?.rendered || ""),
    image: getFeaturedImage(post),
    category: getPrimaryCategory(post),
    categories: getCategoryNames(post),
    author: getAuthorName(post),
    date: formatDate(post.date),
    readTime: getReadTime(post),
    raw: post,
  };
}

function getSearchablePostText(post) {
  if (!post) return "";

  return [
    post.title,
    post.slug,
    post.excerpt,
    post.category,
    ...(post.categories || []),
    post.raw?.link,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function getBlogSeo(post) {
  if (!post) {
    return {
      title: "Square Trigger Blogs",
      description: "Read the latest blog from Square Trigger.",
      ogImage: DEFAULT_BLOG_OG_IMAGE,
    };
  }

  const searchableText = getSearchablePostText(post);

  const matchedSeo = BLOG_SEO_OVERRIDES.find((seoItem) =>
    seoItem.matchedKeywords.some((keyword) =>
      searchableText.includes(keyword.toLowerCase())
    )
  );

  return {
    title: matchedSeo?.title || `${post.title} | Square Trigger Blogs`,
    description:
      matchedSeo?.description ||
      post.excerpt ||
      "Read the latest blog from Square Trigger.",
    ogImage: post.image || DEFAULT_BLOG_OG_IMAGE,
  };
}

function getBlogImageAlt(post) {
  if (!post) return "Square Trigger blog image";

  const searchableText = getSearchablePostText(post);

  const matchedAlt = BLOG_IMAGE_ALT_OVERRIDES.find((item) =>
    item.matchedKeywords.some((keyword) =>
      searchableText.includes(keyword.toLowerCase())
    )
  );

  return matchedAlt?.alt || post.title || "Square Trigger blog image";
}

export default function BlogPage() {
  const { id } = useParams();
  const isDetailPage = Boolean(id);

  const [searchQuery, setSearchQuery] = React.useState("");
  const [activeCategory, setActiveCategory] = React.useState("All");
  const [posts, setPosts] = React.useState([]);
  const [singlePost, setSinglePost] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  const blogSeo = React.useMemo(() => getBlogSeo(singlePost), [singlePost]);

  React.useEffect(() => {
    let isMounted = true;

    async function fetchData() {
      setLoading(true);
      setError("");

      try {
        if (isDetailPage) {
          setSinglePost(null);

          const endpoint = /^\d+$/.test(id)
            ? `${API_BASE}/posts/${id}?_embed`
            : `${API_BASE}/posts?slug=${encodeURIComponent(id)}&_embed`;

          const response = await fetch(endpoint);
          if (!response.ok) throw new Error("Failed to fetch blog post.");

          const data = await response.json();
          const post = Array.isArray(data) ? data[0] : data;

          if (!post) throw new Error("Blog post not found.");
          if (isMounted) setSinglePost(normalizePost(post));
        } else {
          const response = await fetch(
            `${API_BASE}/posts?_embed&per_page=100&orderby=date&order=desc`
          );

          if (!response.ok) throw new Error("Failed to fetch blog posts.");

          const data = await response.json();

          if (isMounted) {
            setPosts(Array.isArray(data) ? data.map(normalizePost) : []);
          }
        }
      } catch (err) {
        if (isMounted) {
          setError(err.message || "Something went wrong while loading blogs.");
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [id, isDetailPage]);

  const categories = React.useMemo(() => {
    const allCategories = posts.flatMap((post) => post.categories || []);
    return ["All", ...Array.from(new Set(allCategories))];
  }, [posts]);

  const filteredPosts = React.useMemo(() => {
    return posts.filter((post) => {
      const query = searchQuery.trim().toLowerCase();

      const matchesSearch =
        !query ||
        post.title.toLowerCase().includes(query) ||
        post.excerpt.toLowerCase().includes(query) ||
        post.category.toLowerCase().includes(query);

      const matchesCategory =
        activeCategory === "All" || post.categories.includes(activeCategory);

      return matchesSearch && matchesCategory;
    });
  }, [posts, searchQuery, activeCategory]);

  if (isDetailPage) {
    return (
      <Layout>
        <SEO
          title={blogSeo.title}
          description={blogSeo.description}
          ogTitle={blogSeo.title}
          ogDescription={blogSeo.description}
          ogSiteName="Square Trigger Blogs"
          ogImage={blogSeo.ogImage}
          ogImageAlt={getBlogImageAlt(singlePost)}
          keywords={SEO_KEYWORDS?.blog || "Square Trigger blog"}
        />

        <style>{`
          .blog-detail-content,
          .blog-detail-content * {
            color: rgba(255,255,255,0.86) !important;
          }

          .blog-detail-content h1,
          .blog-detail-content h2,
          .blog-detail-content h3,
          .blog-detail-content h4,
          .blog-detail-content h5,
          .blog-detail-content h6 {
            color: #ffffff !important;
            font-family: "Space Grotesk", "Inter", sans-serif !important;
            font-weight: 800 !important;
            line-height: 1.18 !important;
            margin-top: 3rem !important;
            margin-bottom: 1.15rem !important;
          }

          .blog-detail-content h2 {
            font-size: clamp(2rem, 3vw, 2.75rem) !important;
          }

          .blog-detail-content h3 {
            font-size: clamp(1.45rem, 2vw, 2rem) !important;
          }

          .blog-detail-content p {
            font-size: clamp(1.05rem, 1.4vw, 1.22rem) !important;
            line-height: 1.85 !important;
            margin-bottom: 1.6rem !important;
          }

          .blog-detail-content ul,
          .blog-detail-content ol {
            padding-left: 1.5rem !important;
            margin: 1.5rem 0 2rem !important;
          }

          .blog-detail-content li {
            font-size: clamp(1.05rem, 1.4vw, 1.22rem) !important;
            line-height: 1.8 !important;
            margin-bottom: 1rem !important;
          }

          .blog-detail-content strong,
          .blog-detail-content b {
            color: #ffffff !important;
            font-weight: 800 !important;
          }

          .blog-detail-content a {
            color: hsl(var(--primary)) !important;
            text-decoration: underline !important;
            text-underline-offset: 4px !important;
          }

          .blog-detail-content img {
            width: 100% !important;
            max-width: 100% !important;
            height: auto !important;
            border-radius: 1.25rem !important;
            margin: 2rem auto !important;
            border: 1px solid rgba(255,255,255,0.12) !important;
          }

          .blog-detail-content figure {
            margin: 2rem 0 !important;
          }
        `}</style>

        <section className="relative overflow-hidden bg-foreground">
          <div className="absolute inset-0 pointer-events-none">
            <div
              className="absolute inset-0 bg-cover bg-center opacity-25"
              style={{ backgroundImage: `url(${HERO_IMAGE})` }}
            />
            <div className="absolute inset-0 bg-gradient-to-br from-foreground via-foreground/95 to-[#101858]" />
            <div className="absolute top-24 right-10 h-80 w-80 rounded-full bg-primary/20 blur-3xl" />
            <div className="absolute bottom-32 left-10 h-72 w-72 rounded-full bg-secondary/15 blur-3xl" />
          </div>

          <div className="relative z-10 mx-auto w-full px-5 py-12 sm:py-16 lg:w-[75%] lg:px-0 lg:py-20">
            <Link
              to="/blogs"
              className="mb-8 inline-flex items-center gap-2 text-sm font-medium text-white/75 transition-colors hover:text-white"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Blogs
            </Link>

            {loading ? (
              <div className="py-20 text-center">
                <p className="text-white/75">Loading blog post...</p>
              </div>
            ) : error ? (
              <div className="py-20 text-center">
                <p className="mb-4 text-red-300">{error}</p>
                <Link to="/blogs">
                  <Button variant="glass" className="text-white">
                    Go Back
                  </Button>
                </Link>
              </div>
            ) : singlePost ? (
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <Badge className="mb-5 border border-primary/30 bg-primary/20 text-primary-foreground">
                  {singlePost.category}
                </Badge>

                <h1 className='mb-6 max-w-[1050px] font-["Lato",system-ui,sans-serif] text-[clamp(2.2rem,4vw,4rem)] font-extrabold leading-[1.12] text-white'>
                  {singlePost.title}
                </h1>

                <div className="mb-8 flex flex-wrap items-center gap-4 text-sm text-white/70">
                  <span>{singlePost.author}</span>

                  <span className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    {singlePost.date}
                  </span>

                  <span className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    {singlePost.readTime}
                  </span>
                </div>

                <div className="mb-12 overflow-hidden rounded-2xl border border-white/10 bg-white/5 shadow-soft">
                  <img
                    src={singlePost.image}
                    alt={getBlogImageAlt(singlePost)}
                    className="max-h-[560px] w-full object-cover"
                  />
                </div>

                <article
                  className="blog-detail-content max-w-none"
                  dangerouslySetInnerHTML={{ __html: singlePost.content }}
                />
              </motion.div>
            ) : (
              <div className="py-20 text-center">
                <p className="text-white/75">Blog post not found.</p>
              </div>
            )}
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <SEO
        title="Square Trigger Blogs | digital agency in vancouver"
        description="Read about the latest blogs in website development, custom web application development, digital marketing, and more."
        ogTitle="Square Trigger Blogs | digital agency in vancouver"
        ogDescription="Read about the latest blogs in website development, custom web application development, digital marketing, and more."
        ogSiteName="Square Trigger Blogs"
        ogImage="https://squaretrigger.com/blog.webp"
        ogImageAlt="Square Trigger Blogs"
        keywords={SEO_KEYWORDS?.blog || "Square Trigger blog"}
      />

      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${HERO_IMAGE})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-br from-foreground/80 via-foreground/70 to-foreground/60" />
        </div>

        <div className="container-custom relative z-10 px-5 pt-[20px] pb-[44px] sm:px-5 sm:pt-[90px] sm:pb-[56px] lg:pt-[60px] lg:pb-[88px]">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mx-auto max-w-[900px] text-center"
          >
            <Badge className="mb-6 border border-primary/30 bg-primary/20 text-primary-foreground">
              Our Blog
            </Badge>

            <h1 className='mb-6 font-["Lato",system-ui,sans-serif] text-[clamp(2.2rem,4vw,3.4rem)] font-extrabold leading-[1.12] text-background'>
              Insights & <span className="gradient-text">Resources</span>
            </h1>

            <p className="mx-auto mb-8 max-w-[720px] text-center text-lg leading-relaxed text-white/85 backdrop-blur-[2px] drop-shadow-[0_2px_8px_rgba(0,0,0,0.45)]">
              Stay updated with the latest trends, tips, and insights in digital
              marketing, web development, and design.
            </p>

            <div className="relative mx-auto max-w-md">
              <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-background/50" />

              <Input
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-12 border-white/20 bg-white/10 pl-12 text-white placeholder:text-white/50"
              />
            </div>
          </motion.div>
        </div>
      </section>

      <section className="border-b border-border bg-background py-8">
        <div className="container-custom">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((category) => (
              <Button
                key={category}
                variant={activeCategory === category ? "default" : "ghost"}
                size="sm"
                onClick={() => setActiveCategory(category)}
              >
                {category}
              </Button>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding">
        <div className="container-custom">
          {loading ? (
            <div className="py-16 text-center">
              <p className="text-muted-foreground">Loading blogs...</p>
            </div>
          ) : error ? (
            <div className="py-16 text-center">
              <p className="text-red-500">{error}</p>
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="py-16 text-center">
              <p className="text-muted-foreground">
                No articles found matching your criteria.
              </p>
            </div>
          ) : (
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {filteredPosts.map((post, index) => (
                <motion.article
                  key={post.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.08 }}
                >
                  <Link to={`/blogs/${post.id}`} className="group block">
                    <Card className="card-hover flex h-full flex-col overflow-hidden border-border transition-all duration-300 hover:border-primary/30">
                      <div className="relative aspect-[16/10] overflow-hidden">
                        <img
                          src={post.image}
                          alt={getBlogImageAlt(post)}
                          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                          loading="lazy"
                        />

                        <div className="absolute left-4 top-4">
                          <Badge
                            variant="secondary"
                            className="bg-background/90 backdrop-blur-sm"
                          >
                            {post.category}
                          </Badge>
                        </div>
                      </div>

                      <div className="flex flex-grow flex-col p-6">
                        <h2 className="mb-3 line-clamp-2 text-xl font-display font-semibold text-foreground transition-colors group-hover:text-primary">
                          {post.title}
                        </h2>

                        <p className="mb-4 line-clamp-3 flex-grow text-sm text-muted-foreground">
                          {post.excerpt}
                        </p>

                        <div className="mt-auto flex items-center justify-between gap-3 border-t border-border pt-4">
                          <div className="flex items-center gap-4 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {post.date}
                            </span>

                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {post.readTime}
                            </span>
                          </div>

                          <span className="inline-flex items-center gap-1 text-sm font-medium text-primary">
                            Read More
                            <ArrowRight className="h-4 w-4" />
                          </span>
                        </div>
                      </div>
                    </Card>
                  </Link>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="relative overflow-hidden bg-background py-12 sm:py-14 lg:py-16">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative overflow-hidden rounded-[28px] bg-gradient-to-r from-[#7c1fff] via-[#4c4cf4] to-[#25a9f5] px-6 py-14 text-center shadow-soft sm:px-10 sm:py-16 lg:px-16 lg:py-20"
          >
            <div className="pointer-events-none absolute inset-0">
              <div className="absolute top-0 left-0 h-80 w-80 rounded-full bg-white/10 blur-3xl sm:h-96 sm:w-96" />
              <div className="absolute right-0 bottom-0 h-80 w-80 rounded-full bg-white/10 blur-3xl sm:h-96 sm:w-96" />
            </div>

            <div className="relative z-10 mx-auto max-w-[1000px]">
              <h2 className="mb-4 text-3xl font-display font-bold text-primary-foreground sm:mb-6 md:text-4xl">
                Stay Updated with Our Latest Insights
              </h2>

              <p className="mx-auto mb-8 max-w-[760px] text-base leading-relaxed text-primary-foreground/85 sm:text-lg">
                Explore more blogs on development, SEO, and digital growth.
              </p>

              <Link to="/contact">
                <Button
                  variant="premium"
                  className="bg-background text-foreground hover:bg-background/90"
                >
                  Contact Us
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}